import { day } from '@vrplatform/utils';
import type { Maybe } from '@vrplatform/utils';
import { Text } from '@mantine/core';
import type { DateFormatter, DatesRangeValue } from '@mantine/dates';
import { type ComponentProps, useCallback } from 'react';
import { InputDay } from './InputDay';

type InputProps = ComponentProps<typeof InputDay>;

type Props = {
  value: Maybe<string>;
  onChange: (v: string | null) => void;
  minDate?: Maybe<string>;
  disabled?: boolean;
  clearable?: boolean;
  placeholder?: string;
  error?: boolean | string;
  width?: InputProps['w'];
  valueFormatter?: InputProps['valueFormatter'];
  singleDateType?: 'range-forever' | 'single-date';
};

export const InputDateRange = ({
  value,
  onChange,
  disabled,
  minDate,
  clearable,
  placeholder,
  width = 240,
  error,
  valueFormatter: propsValueFormatter,
  singleDateType = 'range-forever',
}: Props) => {
  const formatter = useCallback<DateFormatter>(
    (props) => {
      if (propsValueFormatter) return propsValueFormatter(props);

      return valueFormatter(props, disabled, singleDateType);
    },
    [disabled, propsValueFormatter, singleDateType]
  );

  return (
    <InputDay
      type="range"
      allowSingleDateInRange={true}
      value={formatValueToDateRange(value)}
      onChange={(v) => {
        onChange(formatDateRangeToValue(v, value));
      }}
      w={width}
      disabled={disabled}
      placeholder={placeholder}
      clearable={clearable}
      minDate={
        minDate && day(minDate).isValid() ? day(minDate).toDate() : undefined
      }
      valueFormatter={formatter}
      error={error}
    />
  );
};

const valueFormatter = (
  { type, date }: Parameters<DateFormatter>[0],
  disabled: boolean | undefined,
  singleDateType: Props['singleDateType']
) => {
  if (type !== 'range' || Array.isArray(date) === false) return '';

  const start = date[0];
  const end = date[1];

  if (!start) return '';

  if (end) {
    return (
      <Text
        c={disabled ? 'gray' : undefined}
      >{`${day(start).format('ll')} - ${day(end).format('ll')}`}</Text>
    );
  }

  if (singleDateType === 'single-date') {
    return (
      <Text c={disabled ? 'gray' : undefined}>{day(start).format('ll')}</Text>
    );
  }

  return (
    <Text c={disabled ? 'gray' : undefined}>
      {day(start).format('ll')} -{' '}
      <Text span c="gray">
        Forever
      </Text>
    </Text>
  ) as any;
};

const formatValueToDateRange = (value: Maybe<string>): DatesRangeValue => {
  if (!value) return [null, null];

  if (value.includes('...')) {
    const splitted = value.split('...');
    return [day(splitted[0]).toDate(), day(splitted[1]).toDate()];
  }

  return [day(value).toDate(), null];
};

const formatDateRangeToValue = (
  value: DatesRangeValue,
  previousValue: Maybe<string>
): string | null => {
  const start = value[0];
  const end = value[1];

  if (!start && !end) return null;

  if (!end) {
    if (day(start).isValid() === false) return previousValue ?? null;

    return day(start).yyyymmdd();
  }

  const startFormatted = day(start).yyyymmdd();
  const endFormatted = day(end).yyyymmdd();

  if (startFormatted === endFormatted) {
    return startFormatted;
  }

  return `${startFormatted}...${endFormatted}`;
};
